import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import type { SourceHealth } from '@joker/contracts'
import { useTRPC, queryClient } from '../trpc'
import { Panel, Tile, Loading, ErrorNote, fmtDate, ViewTitle } from '../ui'

const DOT: Record<string, string> = { ok: 'ok', stale: 'warn', failing: 'bad', never: 'bad' }

export function Health() {
  const trpc = useTRPC()
  const q = useQuery(trpc.health.data.queryOptions())
  const merge = useMutation(
    trpc.peopleAdmin.merge.mutationOptions({ onSuccess: () => void queryClient.invalidateQueries() }),
  )
  const recompute = useMutation(
    trpc.admin.recompute.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries(),
    }),
  )

  if (q.error) return <ErrorNote error={q.error} />
  if (!q.data) return <Loading what="data health" />

  const h = q.data
  const unmatchedTotal = h.unmatchedBySource.reduce((a, b) => a + b.count, 0)

  return (
    <>
      <ViewTitle suit="joker-red">Data health</ViewTitle>
      <p className="view-sub">
        The unglamorous screen. If this one isn’t honest, nobody trusts the other five — so it ships
        in Phase 1, not later.
      </p>

      <div
        className="chip"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 14,
          padding: '8px 14px',
          fontSize: 14,
          background: h.allOk ? 'color-mix(in srgb, var(--good) 18%, transparent)' : 'color-mix(in srgb, var(--warning) 18%, transparent)',
        }}
      >
        <span className={`dot ${h.allOk ? 'ok' : 'warn'}`} style={{ display: 'inline-block' }} />
        {h.allOk
          ? 'All four sources are fresh: Cortex, Klaviyo, Wix and mail.'
          : `Not fresh: ${h.sources.filter((s) => s.status !== 'ok').map((s) => s.label).join(', ')}.`}
      </div>

      <div className="tiles">
        {h.unmatchedBySource.map((u) => (
          <Tile
            key={u.source}
            k={`Unmatched ${u.source}`}
            v={u.count}
            d="needs a human to link"
          />
        ))}
        {unmatchedTotal === 0 && <Tile k="Unmatched records" v={0} d="nothing waiting" />}
        <Tile k="Buyers with no channel" v={h.paidWithoutChannel} d="attribution gap" />
        <Tile k="Patients with no phone" v={h.peopleWithoutPhone} d="blocks SMS in Phase 2" />
      </div>

      <Panel title="Source freshness" note="the dashboard is only as current as its slowest source">
        <div className="scroll-x">
          <table className="data" style={{ minWidth: 560 }}>
            <thead>
              <tr>
                <th>Source</th>
                <th>Last success</th>
                <th>Last run</th>
                <th className="r">Rows</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {h.sources.map((s) => (
                <tr key={s.source} style={{ cursor: 'default' }}>
                  <td>
                    <span className={`dot ${DOT[s.status]}`} style={{ display: 'inline-block', marginRight: 8 }} />
                    <strong>{s.label}</strong>
                    {s.note && (
                      <>
                        <br />
                        <span className="muted" style={{ fontSize: 11.5 }}>
                          {s.note}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="muted">{fmtDate(s.lastSuccessAt)}</td>
                  <td className="muted">{fmtDate(s.lastRunAt)}</td>
                  <td className="r num">{s.rowsLastRun ?? '—'}</td>
                  <td className="r">
                    <RefreshButton s={s} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="footnote">
          <strong>The open gap:</strong> nothing in the Wix checkout reliably records which practice
          sent a buyer, so {h.paidWithoutChannel} paid patients cannot be attributed. That makes the
          most valuable comparison on the Cohorts screen partly guesswork. A per-practice code at
          checkout, plus a manual override here, closes it.
        </p>

        <button
          className="chip"
          style={{ cursor: 'pointer' }}
          disabled={recompute.isPending}
          onClick={() => recompute.mutate()}
        >
          {recompute.isPending ? 'Queued…' : 'Recompute metrics, stages and buckets'}
        </button>
      </Panel>

      <Panel
        title="Possible duplicate patients"
        note="same name, more than one account"
      >
        {h.duplicateCandidates.length === 0 ? (
          <p className="footnote">Nobody in the list shares a name with anybody else.</p>
        ) : (
          <>
            <div className="scroll-x">
              <table className="data">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th className="r">Oryx ID</th>
                    <th>Email</th>
                    <th>Why a second account</th>
                    <th className="r">Created</th>
                    <th className="r">Sessions</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {h.duplicateCandidates.flatMap((g) => {
                    /* The newest account is the live one and carries the address
                       the team uses, so it survives and the rest point at it. */
                    const keep = g.people[g.people.length - 1]!
                    return g.people.map((p, i) => (
                      <tr key={p.id} className={i === 0 ? 'dup-first' : undefined}>
                        <td>{i === 0 ? <strong>{g.name}</strong> : <span className="muted">↳</span>}</td>
                        <td className="r num">{p.oryxUserNumber ?? '—'}</td>
                        <td className="muted">{p.email ?? 'no email'}</td>
                        <td>{p.note ?? <span className="muted">—</span>}</td>
                        <td className="r num">{fmtDate(p.createdAt)}</td>
                        <td className="r num">{p.sessionsTotal}</td>
                        <td className="r">
                          {i === 0 ? (
                            <button
                              type="button"
                              className="btn"
                              disabled={merge.isPending}
                              onClick={() =>
                                merge.mutate({
                                  keepId: keep.id,
                                  supersededIds: g.people.filter((x) => x.id !== keep.id).map((x) => x.id),
                                })
                              }
                            >
                              Merge into {keep.email ?? 'newest'}
                            </button>
                          ) : p.id === keep.id ? (
                            <span className="muted">stays</span>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  })}
                </tbody>
              </table>
            </div>
            <p className="footnote">
              Oryx creates a second account on purpose when someone starts a new program or a
              training condition changes — the reason is often written into the name. These are
              reported, never joined automatically: some pairs here are typos rather than
              re-enrolments, and only a person can tell the difference.
            </p>
          </>
        )}
      </Panel>
    </>
  )
}

/**
 * Pull from one source now. A press starts a two-hour cooldown, enforced by
 * the server rather than here: Klaviyo and Wix meter their APIs, and a button
 * that can be held down is one that will be.
 */
function RefreshButton({ s }: { s: SourceHealth }) {
  const trpc = useTRPC()
  const [justRan, setJustRan] = useState(false)
  const refresh = useMutation(
    trpc.health.refresh.mutationOptions({
      onSuccess: async () => {
        setJustRan(true)
        await queryClient.invalidateQueries()
        window.setTimeout(() => setJustRan(false), 4000)
      },
    }),
  )
  const cooling = s.cooldownSeconds > 0
  const mins = Math.ceil(s.cooldownSeconds / 60)
  return (
    <>
      <button
        type="button"
        className="btn sm"
        disabled={refresh.isPending || cooling || !s.canRefresh}
        title={cooling ? `Refreshed recently. Again in ${mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`}.` : (s.note ?? `Pull from ${s.label} now`)}
        onClick={() => refresh.mutate({ source: s.source as 'klaviyo' })}
      >
        {refresh.isPending ? 'Starting…' : justRan ? 'Queued' : 'Refresh'}
      </button>
      {cooling && (
        <>
          <br />
          <span className="muted" style={{ fontSize: 11.5 }}>
            again in {mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`}
          </span>
        </>
      )}
      {refresh.error && (
        <>
          <br />
          <span className="bad-text">{refresh.error.message}</span>
        </>
      )}
    </>
  )
}
